import React, { useEffect, useState } from 'react'
import {
  AddToExistingMenuCardProgressStatus,
  ChefConcept,
  ChefScheduledOrder,
  ChefSearchMenuItem,
} from '@types'
import Modal from '@components/common/modal/Modal'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import Moment from 'moment-timezone'
import Loader from '@components/common/Loader'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

interface RadioGroupProps extends React.ComponentProps<'div'> {
  className?: string
  value: string
  onValueChange: (value: string) => void
}

function RadioGroup({
  value,
  onValueChange,
  className,
  ...props
}: RadioGroupProps) {
  return (
    <div
      data-slot="radio-group"
      className={cn('grid gap-3', className)}
      onChange={() => onValueChange(value)}
      {...props}
    />
  )
}

interface RadioGroupItemProps extends React.ComponentProps<'div'> {
  className?: string
  value: string
  selectedMenuCardId: string
}
function RadioGroupItem({
  value,
  selectedMenuCardId,
  className,
  ...props
}: RadioGroupItemProps) {
  const fillPrimary =
    selectedMenuCardId && value === selectedMenuCardId ? 'fill-primary' : ''

  return (
    <div
      data-slot="radio-group-item"
      className={cn(
        'border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <div
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`lucide lucide-circle absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 ${fillPrimary}`}
        >
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
      </div>
    </div>
  )
}

interface AddToExistingMenuCardProps {
  stagedMenuItems: ChefSearchMenuItem[]
  menuConcepts: ChefConcept[]
  chefId: string
  showAddToExistingMenuCard: boolean
  onClose: () => void

  calcMargin: (item: ChefSearchMenuItem) => number
  asyncLoadConcept: (conceptId: string) => any
  saveMenuConcept: (data: any) => any
  loadChefOrders: (req: {
    chefId: string
    calendarStartDate: string
    calendarEndDate: string
    withoutItems: boolean
  }) => ChefScheduledOrder[]
  loadConcepts: () => void
}

const AddToExistingMenuCard: React.FC<AddToExistingMenuCardProps> = ({
  stagedMenuItems,
  menuConcepts,
  chefId,
  showAddToExistingMenuCard,
  onClose,
  calcMargin,

  asyncLoadConcept,
  saveMenuConcept,
  loadChefOrders,
  loadConcepts,
}) => {
  const [currentProgressStatus, setCurrentProgressStatus] =
    useState<AddToExistingMenuCardProgressStatus>(
      AddToExistingMenuCardProgressStatus.Select,
    )
  const [selectedMenuCard, setSelectedMenuCard] = useState<string>('')
  const [stagedMenuItemIds, setStagedMenuItemIds] = useState<string[]>(
    stagedMenuItems.map((item) => item.id),
  )
  const [activeOrders, setActiveOrders] = useState<ChefScheduledOrder[]>([])
  const [conceptsLoading, setConceptsLoading] = useState<boolean>(false)
  const [saveMenuConceptLoading, setSaveMenuConceptLoading] =
    useState<boolean>(false)
  const [futureOrdersLoading, setFutureOrdersLoading] = useState<boolean>(false)

  useEffect(() => {
    const run = async () => {
      setFutureOrdersLoading(true)
      const today = Moment()
      const formattedToday = today.format('MM/DD/YYYY')
      const plus30 = today.clone().add(30, 'days').format('MM/DD/YYYY')

      const orders = await loadChefOrders({
        chefId,
        calendarStartDate: formattedToday,
        calendarEndDate: plus30,
        withoutItems: false,
      })
      setActiveOrders(orders)
      setFutureOrdersLoading(false)
    }

    run()
  }, [chefId, loadChefOrders])

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      setConceptsLoading(true)
      await loadConcepts()
      if (isMounted) {
        setConceptsLoading(false)
      }
    }

    run()

    return () => {
      isMounted = false
    }
  }, [loadConcepts])

  const onHide = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    onClose()
  }

  const toggleMenuItem = (itemId: string) => {
    if (stagedMenuItemIds.includes(itemId)) {
      setStagedMenuItemIds(stagedMenuItemIds.filter((id) => id !== itemId))
    } else {
      setStagedMenuItemIds([...stagedMenuItemIds, itemId])
    }
  }

  const handleSave = () => {
    const selectedCard = menuConcepts.find(
      (card) => card.id === selectedMenuCard,
    )

    if (selectedCard) {
      setCurrentProgressStatus(AddToExistingMenuCardProgressStatus.Confirm)
    }
  }

  const backToSelectProgressStatus = () => {
    setCurrentProgressStatus(AddToExistingMenuCardProgressStatus.Select)
  }

  const getCurrentSelectedMenuCard = () => {
    if (selectedMenuCard === '' || menuConcepts.length === 0) {
      return undefined
    }

    return menuConcepts.find((card) => card.id === selectedMenuCard)
  }

  const getNewMenuItemsToAddToSelectedMenuCard = (
    menuItems: ChefSearchMenuItem[],
  ) => {
    if (menuItems.length === 0 || selectedCard === undefined) {
      return []
    }

    return menuItems.filter(
      (item) =>
        !selectedCard?.menuItems.map((i: any) => i.id).includes(item.id) &&
        selectedCard?.marketType === item.marketType,
    )
  }

  const getOrderCounts = (order: any) => {
    const menu = order.menus.find(
      (menu: any) => menu.menuId === selectedMenuCard,
    )

    if (menu === undefined) {
      return 0
    }

    let count = 0
    menu.menuItems.forEach((item: any) => {
      const itemName = item.name
      const itemCount = order.items.find((it: any) => it.item === itemName)
      if (itemCount) {
        count += parseInt(itemCount.quantity)
      }
    })

    return count
  }

  const selectedCard = getCurrentSelectedMenuCard()
  const newItemsToAdd = getNewMenuItemsToAddToSelectedMenuCard(stagedMenuItems)

  const submitToMenuCard = async () => {
    setSaveMenuConceptLoading(true)
    const concept = await asyncLoadConcept(selectedMenuCard)
    const newItems = getNewMenuItemsToAddToSelectedMenuCard(stagedMenuItems)
    const menusize = concept.conceptsMenuItems.length
    concept.conceptsMenuItems.forEach((item: any, index: number) => {
      item.position = index + 1
    })
    newItems.forEach((item: any, index: number) => {
      concept.conceptsMenuItems.push({
        menuItem: {
          id: item.id,
        },
        position: menusize + (1 + index),
      })
    })
    await saveMenuConcept(concept)
    setSaveMenuConceptLoading(false)
    setCurrentProgressStatus(AddToExistingMenuCardProgressStatus.Select)
    onClose()
  }

  const getActiveOrdersFromSelectedMenuCard = () => {
    if (selectedMenuCard && activeOrders.length > 0) {
      return activeOrders.filter((o) =>
        o.menus.map((m) => m.menuId).includes(selectedMenuCard),
      )
    }

    return []
  }

  return (
    <>
      {showAddToExistingMenuCard && (
        <Modal
          title={`ADD MENU ITEMS TO EXISTING MENU CARD (TOTAL # MENUS: ${menuConcepts.length})`}
          color="#001940"
          width="800px"
          hideModal={onHide}
        >
          <div className="px-6 py-6 max-h-[80vh] overflow-y-auto">
            {futureOrdersLoading && (
              <>
                Please wait. Loading future chef orders...
                <Loader width="50px" isCenter={true} />
              </>
            )}
            {currentProgressStatus ===
              AddToExistingMenuCardProgressStatus.Select && (
              <>
                {conceptsLoading && (
                  <>
                    Loading menu cards
                    <Loader width="50px" isCenter={true} />
                  </>
                )}
                {!conceptsLoading && menuConcepts.length > 0 && (
                  <div className="mb-6 pb-6 border-b">
                    <label className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 block text-xs font-semibold text-gray-700 mb-3">
                      SELECT EXISTING MENU CARD
                    </label>
                    <RadioGroup
                      value={selectedMenuCard}
                      onValueChange={setSelectedMenuCard}
                    >
                      <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
                        {menuConcepts.map((card) => {
                          const cardDuplicates = stagedMenuItems.filter(
                            (item) =>
                              card.menuItems.map((i) => i.id).includes(item.id),
                          )
                          const cardNewItems = stagedMenuItems.filter(
                            (item) =>
                              !card.menuItems
                                .map((i) => i.id)
                                .includes(item.id) &&
                              card.marketType === item.marketType,
                          )

                          return (
                            <div
                              key={card.id}
                              onClick={() => setSelectedMenuCard(card.id)}
                              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                                selectedMenuCard === card.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <RadioGroupItem
                                      value={card.id}
                                      selectedMenuCardId={selectedMenuCard}
                                      id={`card-${card.id}`}
                                    />
                                    <label
                                      style={{ marginLeft: '15px' }}
                                      htmlFor={`card-${card.id}`}
                                      className="flex items-center gap-2 text-sm leading-none select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 font-semibold text-gray-900 cursor-pointer"
                                    >
                                      {card.name}
                                    </label>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 ml-6">
                                    Currently has{' '}
                                    <strong>
                                      {card.menuItems.length} items
                                    </strong>
                                  </p>
                                  {selectedMenuCard === card.id && (
                                    <div className="mt-2 ml-6 space-y-1">
                                      {cardNewItems.length > 0 && (
                                        <p className="text-sm text-green-700">
                                          ✓ Will add{' '}
                                          <strong>
                                            {cardNewItems.length} new item(s)
                                          </strong>
                                        </p>
                                      )}
                                      {cardDuplicates.length > 0 && (
                                        <div className="text-sm text-orange-700">
                                          <p className="mb-1">
                                            ⚠{' '}
                                            <strong>
                                              {cardDuplicates.length} duplicate
                                              item(s)
                                            </strong>{' '}
                                            will be skipped:
                                          </p>
                                          <ul className="ml-4 space-y-0.5">
                                            {cardDuplicates.map((item) => (
                                              <li
                                                key={item.id}
                                                className="text-xs"
                                              >
                                                • {item.name}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {selectedMenuCard &&
                  newItemsToAdd &&
                  newItemsToAdd.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xs font-semibold text-gray-700 mb-4">
                        ITEMS TO ADD{' '}
                        <span className="text-green-700 ml-2">
                          ({newItemsToAdd.length} new items)
                        </span>
                      </h3>
                      <div className="space-y-3">
                        {newItemsToAdd.map((item) => {
                          const isExpanded = stagedMenuItemIds.includes(item.id)

                          return (
                            <div
                              key={item.id}
                              className="border rounded-lg overflow-hidden"
                            >
                              <div className="flex items-center justify-between p-4 bg-gray-50">
                                <div className="flex items-center gap-3 flex-1">
                                  <button
                                    onClick={() => toggleMenuItem(item.id)}
                                    className="text-black-400 hover:text-black-600"
                                  >
                                    {isExpanded ? (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="lucide lucide-chevron-up w-4 h-4"
                                      >
                                        <path d="m18 15-6-6-6 6"></path>
                                      </svg>
                                    ) : (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="lucide lucide-chevron-down w-4 h-4"
                                      >
                                        <path d="m6 9 6 6 6-6"></path>
                                      </svg>
                                    )}
                                  </button>
                                  <span className="font-medium text-gray-900">
                                    {item.name}
                                  </span>
                                </div>
                              </div>

                              {isExpanded && (
                                <div className="p-4 bg-white flex gap-4">
                                  <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs">
                                        Meal Type:
                                      </span>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {item.mealType}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs">
                                        Market Type:
                                      </span>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                        {item.marketType}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs">
                                        Group Order Meal Types:
                                      </span>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        {item.tagsGroupOrderMealTypeList}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs">Cuisine:</span>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        {item.tagsCuisineList}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-2">
                                      <strong>Description:</strong>{' '}
                                      {item.description}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-2">
                                      <strong>Chef Price:</strong> $
                                      {parseFloat(item.chefPrice).toFixed(2)} |{' '}
                                      <strong>Market Price:</strong> $
                                      {parseFloat(item.marketPrice).toFixed(2)}{' '}
                                      | <strong>Margin:</strong>{' '}
                                      {calcMargin(item).toFixed(2)}%
                                      <div className="text-xs text-gray-500 mt-1">
                                        (
                                        {parseFloat(item.marketPrice).toFixed(
                                          2,
                                        )}{' '}
                                        Avg Price Per Person)
                                      </div>
                                    </div>
                                  </div>
                                  {item.images && item.images.length > 0 && (
                                    <div className="w-32 h-32 flex-shrink-0">
                                      <img
                                        src={
                                          item.images[0].thumborUrl ||
                                          '/placeholder.svg'
                                        }
                                        alt={item.name}
                                        className="w-full h-full object-cover rounded"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                  <button onClick={onClose}>Cancel</button>
                  <button
                    className="btn bg-green-600 hover:bg-green-700 text-white hover:text-white"
                    disabled={!selectedMenuCard || newItemsToAdd.length === 0}
                    onClick={handleSave}
                  >
                    Add {newItemsToAdd.length} Item
                    {newItemsToAdd.length !== 1 ? 's' : ''} to Menu Card
                  </button>
                </div>
              </>
            )}

            {currentProgressStatus ===
              AddToExistingMenuCardProgressStatus.Confirm &&
              selectedCard && (
                <>
                  {saveMenuConceptLoading && (
                    <>
                      Saving menu card...
                      <Loader width="50px" isCenter={true} />
                    </>
                  )}
                  {getActiveOrdersFromSelectedMenuCard().length === 0 && (
                    <>
                      <div className="mb-6 p-4 bg-green-100 border-2 border-green-400 rounded-lg">
                        <p className="text-sm text-gray-900">
                          <strong className="font-semibold">
                            Menu card not scheduled on upcoming group orders!
                          </strong>
                        </p>
                      </div>
                    </>
                  )}
                  {getActiveOrdersFromSelectedMenuCard().length > 0 && (
                    <>
                      <div className="mb-6 p-4 bg-yellow-100 border-2 border-yellow-400 rounded-lg">
                        <p className="text-sm text-gray-900">
                          <strong className="font-semibold">
                            ⚠ Action Required:
                          </strong>{' '}
                          This menu card is currently scheduled on upcoming
                          group orders. To reflect the additional menu items on
                          live menus, you must manually sync the menu on each
                          order. If not synced, the additional items will only
                          appear when the menu is scheduled next.
                        </p>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-xs font-semibold text-gray-700 mb-3">
                          ORDERS REQUIRING MENU SYNC
                        </h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                                  GROUP ORDER NUMBER
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                                  STATUS
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                                  ORDER COUNT
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {getActiveOrdersFromSelectedMenuCard().map(
                                (order, index) => (
                                  <tr
                                    key={order.orderNumber}
                                    className={
                                      index % 2 === 0
                                        ? 'bg-white'
                                        : 'bg-gray-50'
                                    }
                                  >
                                    <td className="px-4 py-3">
                                      <span className="text-blue-600 hover:text-blue-800 font-medium text-sm underline">
                                        #{order.orderNumber}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {order.status === 'Finalized'
                                          ? 'Active'
                                          : order.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {getOrderCounts(order)} orders
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                    <button onClick={backToSelectProgressStatus}>Back</button>
                    <button
                      className="btn bg-green-600 hover:bg-green-700 text-white"
                      onClick={submitToMenuCard}
                      disabled={saveMenuConceptLoading}
                    >
                      Confirm & Save
                    </button>
                  </div>
                </>
              )}
          </div>
        </Modal>
      )}
    </>
  )
}

export default AddToExistingMenuCard
